import { Component, inject, Inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import {
  EmailLabelingConfigListComponent,
  EmailLabelingConfigService,
  MessageCategoryConfig,
} from '@galaxy/email-ui/email-labeling-config';

@Component({
  selector: 'app-email-labeling-settings-list',
  standalone: true,
  imports: [GalaxyPageModule, TranslateModule, EmailLabelingConfigListComponent],
  templateUrl: './email-labeling-settings-list.component.html',
  styleUrls: ['./email-labeling-settings-list.component.scss'],
})
export class EmailLabelingSettingsListComponent {
  private readonly router = inject(Router);
  private readonly service = inject(EmailLabelingConfigService);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly reloadTrigger = signal(0);

  constructor(
    @Inject('PARTNER_ID') protected readonly partnerId$: Observable<string>,
    @Inject('USER_ID') protected readonly userId$: Observable<string>,
  ) {}

  protected readonly namespace = toSignal(this.partnerId$, { initialValue: '' });
  protected readonly userId = toSignal(this.userId$, { initialValue: '' });

  protected onAddNew(): void {
    this.router.navigate(['/email-labeling-settings/new']);
  }

  protected onEdit(config: MessageCategoryConfig): void {
    this.router.navigate(['/email-labeling-settings', config.configurationId]);
  }

  protected onDelete(config: MessageCategoryConfig): void {
    const modalConfig = this.createDeleteConfirmationConfig(config.name);

    this.confirmationModal
      .openModal(modalConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteConfig(config.configurationId);
        }
      });
  }

  private createDeleteConfirmationConfig(configName: string) {
    return {
      title: this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.DELETE_CONFIRMATION_TITLE'),
      message: this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.DELETE_CONFIRMATION_MESSAGE', {
        name: configName,
      }),
      confirmButtonText: this.translate.instant('COMMON.DELETE'),
      cancelButtonText: this.translate.instant('COMMON.CANCEL'),
      type: 'warn' as const,
    };
  }

  private deleteConfig(configurationId: string): void {
    this.service
      .deleteConfig(this.namespace(), configurationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackbar.openSuccessSnack(
            this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.DELETE_SUCCESS'),
          );
          this.reloadTrigger.update((value) => value + 1);
        },
        error: () => {
          this.snackbar.openErrorSnack(this.translate.instant('ADMINISTRATION.EMAIL_LABELING_SETTINGS.DELETE_ERROR'));
        },
      });
  }
}
